import * as React from 'react'
import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createClient } from '@/features/admin/api'
import { Drawer } from '@/components/primitives/Drawer'
import { Button } from '@/components/primitives/Button'
import { Input } from '@/components/primitives/Input'
import { PasswordInput } from '@/components/primitives/PasswordInput'
import { FormField } from '@/components/primitives/FormField'
import { useToast } from '@/components/feedback/ToastProvider'
import { ApiError } from '@/lib/api'
import { formatPhoneInput, combinePhoneCode } from '@/lib/phone'
import '../ManagePages.css'

interface Props {
  open: boolean
  onClose: () => void
}

export function AddOrganizationDrawer({ open, onClose }: Props): React.JSX.Element {
  const toast = useToast()
  const qc = useQueryClient()

  const [orgName, setOrgName] = useState('')
  const [facilityName, setFacilityName] = useState('')
  const [facilityAddress, setFacilityAddress] = useState('')
  const [facilityCity, setFacilityCity] = useState('')
  const [facilityState, setFacilityState] = useState('')
  const [facilityZip, setFacilityZip] = useState('')
  const [facilityPhone, setFacilityPhone] = useState('')
  const [facilityPhoneCode, setFacilityPhoneCode] = useState('+1')
  const [adminName, setAdminName] = useState('')
  const [adminEmail, setAdminEmail] = useState('')
  const [adminPassword, setAdminPassword] = useState('')

  function reset(): void {
    setOrgName(''); setFacilityName(''); setFacilityAddress('')
    setFacilityCity(''); setFacilityState(''); setFacilityZip('')
    setFacilityPhone(''); setFacilityPhoneCode('+1'); setAdminName(''); setAdminEmail(''); setAdminPassword('')
  }

  const create = useMutation({
    mutationFn: () =>
      createClient({
        org_name: orgName,
        facility_name: facilityName,
        facility_address: facilityAddress || undefined,
        facility_city: facilityCity || undefined,
        facility_state: facilityState || undefined,
        facility_zip: facilityZip || undefined,
        facility_phone: combinePhoneCode(facilityPhone, facilityPhoneCode) || undefined,
        admin_name: adminName,
        admin_email: adminEmail,
        admin_password: adminPassword || undefined,
      }),
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: ['admin-orgs'] })
      void qc.invalidateQueries({ queryKey: ['admin-facilities'] })
      void qc.invalidateQueries({ queryKey: ['admin-users'] })
      void qc.invalidateQueries({ queryKey: ['clients'] })
      reset()
      onClose()
      if (adminPassword) {
        toast.success('Organization created', `${res.invite_email} can log in immediately.`)
      } else if (!res.email_sent) {
        toast.warning('Created — email failed', 'Use Resend to deliver the invite link.')
      } else if (res.invite_url) {
        toast.success('Organization created', `Dev link: ${res.invite_url}`)
      } else {
        toast.success('Organization created', `Invite sent to ${res.invite_email}.`)
      }
    },
    onError: (err) =>
      toast.error('Could not create', err instanceof ApiError ? err.message : 'Try again.'),
  })

  const canSubmit = orgName.trim() && facilityName.trim() && adminName.trim() && adminEmail.trim()

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Add organization"
      subtitle="Creates the org, first facility, and sends an admin invite."
      footer={
        <>
          <Button variant="ghost" onClick={onClose} type="button" disabled={create.isPending}>Cancel</Button>
          <Button
            onClick={() => create.mutate()}
            loading={create.isPending}
            disabled={!canSubmit}
          >
            {adminPassword ? 'Create & activate' : 'Create & invite'}
          </Button>
        </>
      }
    >
      <div className="drawer-sections">
        {/* Organization */}
        <div className="drawer-section">
          <div className="drawer-section-label">Organization</div>
          <FormField label="Organization name" htmlFor="d-org" required>
            <Input id="d-org" value={orgName} onChange={e => setOrgName(e.target.value)} placeholder="Sunrise Senior Living" />
          </FormField>
        </div>

        {/* First Facility */}
        <div className="drawer-section">
          <div className="drawer-section-label">First facility</div>
          <FormField label="Facility name" htmlFor="d-fac" required>
            <Input id="d-fac" value={facilityName} onChange={e => setFacilityName(e.target.value)} placeholder="Sunrise North" />
          </FormField>
          <div className="drawer-phone-row">
            <FormField label="Code" htmlFor="d-fphone-code">
              <select id="d-fphone-code" className="select" value={facilityPhoneCode} onChange={e => setFacilityPhoneCode(e.target.value)}>
                <option value="+1">+1</option>
                <option value="+91">+91</option>
                <option value="+41">+41</option>
              </select>
            </FormField>
            <FormField label="Phone" htmlFor="d-fphone">
              <Input id="d-fphone" type="tel" value={facilityPhone} onChange={e => setFacilityPhone(formatPhoneInput(e.target.value))} placeholder="(312) 555-0100" />
            </FormField>
          </div>
          <FormField label="Address" htmlFor="d-faddr">
            <Input id="d-faddr" value={facilityAddress} onChange={e => setFacilityAddress(e.target.value)} placeholder="123 Main St" />
          </FormField>
          <div className="drawer-3col">
            <FormField label="City" htmlFor="d-fcity">
              <Input id="d-fcity" value={facilityCity} onChange={e => setFacilityCity(e.target.value)} placeholder="Chicago" />
            </FormField>
            <FormField label="State" htmlFor="d-fstate">
              <Input id="d-fstate" value={facilityState} onChange={e => setFacilityState(e.target.value)} placeholder="IL" maxLength={2} />
            </FormField>
            <FormField label="ZIP" htmlFor="d-fzip">
              <Input id="d-fzip" value={facilityZip} onChange={e => setFacilityZip(e.target.value)} placeholder="60601" />
            </FormField>
          </div>
        </div>

        {/* Admin Account */}
        <div className="drawer-section">
          <div className="drawer-section-label">Admin account</div>
          <div className="drawer-2col">
            <FormField label="Full name" htmlFor="d-aname" required>
              <Input id="d-aname" value={adminName} onChange={e => setAdminName(e.target.value)} placeholder="Carol Admin" />
            </FormField>
            <FormField label="Email address" htmlFor="d-aemail" required>
              <Input id="d-aemail" type="email" value={adminEmail} onChange={e => setAdminEmail(e.target.value)} placeholder="carol@sunrise.com" />
            </FormField>
          </div>
          <FormField label="Set password now (optional)" htmlFor="d-apw" hint="Leave blank to send an invite link by email instead.">
            <PasswordInput id="d-apw" value={adminPassword} onChange={e => setAdminPassword(e.target.value)} placeholder="Leave blank to invite by email" autoComplete="new-password" />
          </FormField>
        </div>
      </div>
    </Drawer>
  )
}
